/**
 * Catálogo de magnitudes que reporta un medidor de energía.
 *
 * Los medidores las nombran con claves técnicas (`RMS_Volts_Ln_1`). Aquí se
 * traducen a lenguaje entendible y se les asocia su unidad, para que quien
 * configure un umbral no tenga que saber esa nomenclatura.
 */
export interface VariableMedidor {
  clave: string;
  etiqueta: string;
  unidad: string;
  grupo: string;
}

export const VARIABLES_MEDIDOR: readonly VariableMedidor[] = [
  { clave: 'RMS_Volts_Ln_1', etiqueta: 'Tensión fase 1', unidad: 'V', grupo: 'Tensión' },
  { clave: 'RMS_Volts_Ln_2', etiqueta: 'Tensión fase 2', unidad: 'V', grupo: 'Tensión' },
  { clave: 'RMS_Volts_Ln_3', etiqueta: 'Tensión fase 3', unidad: 'V', grupo: 'Tensión' },

  { clave: 'Amps_Ln_1', etiqueta: 'Corriente fase 1', unidad: 'A', grupo: 'Corriente' },
  { clave: 'Amps_Ln_2', etiqueta: 'Corriente fase 2', unidad: 'A', grupo: 'Corriente' },
  { clave: 'Amps_Ln_3', etiqueta: 'Corriente fase 3', unidad: 'A', grupo: 'Corriente' },

  { clave: 'RMS_Watts_Tot', etiqueta: 'Potencia total', unidad: 'W', grupo: 'Potencia' },
  { clave: 'RMS_Watts_Ln_1', etiqueta: 'Potencia fase 1', unidad: 'W', grupo: 'Potencia' },
  { clave: 'RMS_Watts_Ln_2', etiqueta: 'Potencia fase 2', unidad: 'W', grupo: 'Potencia' },
  { clave: 'RMS_Watts_Ln_3', etiqueta: 'Potencia fase 3', unidad: 'W', grupo: 'Potencia' },

  { clave: 'Cos_Theta_Ln_1', etiqueta: 'Factor de potencia fase 1', unidad: '', grupo: 'Calidad' },
  { clave: 'Cos_Theta_Ln_2', etiqueta: 'Factor de potencia fase 2', unidad: '', grupo: 'Calidad' },
  { clave: 'Cos_Theta_Ln_3', etiqueta: 'Factor de potencia fase 3', unidad: '', grupo: 'Calidad' },
  { clave: 'Line_Freq', etiqueta: 'Frecuencia de red', unidad: 'Hz', grupo: 'Calidad' },

  { clave: 'kWh_Tot', etiqueta: 'Energía consumida', unidad: 'kWh', grupo: 'Energía' },
  { clave: 'Rev_kWh_Tot', etiqueta: 'Energía entregada', unidad: 'kWh', grupo: 'Energía' },
];

const POR_CLAVE = new Map(VARIABLES_MEDIDOR.map((v) => [v.clave, v]));

/**
 * Descripción de una variable.
 * Si el medidor reporta una que no está en el catálogo, se devuelve su clave
 * tal cual en vez de ocultarla: es preferible mostrar algo técnico a no
 * mostrar nada.
 */
export function describirVariable(clave: string | null | undefined): VariableMedidor {
  if (!clave) return { clave: '', etiqueta: '—', unidad: '', grupo: 'Otras' };
  return POR_CLAVE.get(clave) ?? { clave, etiqueta: clave, unidad: '', grupo: 'Otras' };
}

/** Etiqueta con su unidad, para mostrar un valor completo. */
export function formatearValorVariable(clave: string | null, valor: number | null): string {
  if (valor === null) return '—';

  const { unidad } = describirVariable(clave);
  const texto = Number.isInteger(valor) ? String(valor) : valor.toFixed(2);
  return unidad ? `${texto} ${unidad}` : texto;
}
